import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { PERSONA_IMAGES, getSuggestion } from "@/lib/api";
import { 
  Send, User, TrendingUp, TrendingDown, FlaskConical, Sparkles, 
  Meh, ThumbsDown, Loader2, Mic, Info, ChevronDown, ChevronUp, X,
  Building2, Zap, CheckCircle, AlertTriangle
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";

const PERSONA_CONTEXT = {
  jamie_sterling: {
    name: "Jamie Sterling",
    role: "Managing Director, Helios Capital",
    archetype: "Skeptic / Economic Buyer",
    difficulty: "Hard",
    focus_areas: ["Metrics (M)", "Economic Buyer (E)"],
    background: "Former investment banker with deep expertise in M&A and financial modeling. Jamie is a deal-maker, not an operator, which is why Innovatech's operational challenges are outside his comfort zone.",
    approach: "Expect a fast-paced, direct conversation. Jamie will cut through fluff and demand quantified ROI, proven track records, and fast timelines. He's skeptical of consultants and will push back on vague value propositions.",
    positive_triggers: ["Quantified ROI", "Fast timeline", "Proven track record"],
    negative_triggers: ["Vague promises", "Consultant jargon", "Long explanations"]
  },
  evelyn_reed: {
    name: "Dr. Evelyn Reed",
    role: "Operating Partner, Helios Capital",
    archetype: "Champion / Decision Maker",
    difficulty: "Medium",
    focus_areas: ["Decision Criteria (D1)", "Champion (C1)"],
    background: "PhD in Industrial Engineering and former COO of a major manufacturing conglomerate. Evelyn was brought into Helios to provide operational oversight and feels her advice was ignored during the initial Innovatech due diligence.",
    approach: "A methodical conversation focused on process and evidence. Evelyn will ask detailed 'how' questions about your vetting methodology and risk mitigation. She can become your Champion if you earn her trust through substance.",
    positive_triggers: ["Detailed methodology", "Evidence & data", "Acknowledging complexity"],
    negative_triggers: ["Oversimplification", "Ignoring her expertise", "Lack of due diligence"]
  },
  ben_carter: {
    name: "Ben Carter",
    role: "VP Procurement, Helios Capital",
    archetype: "Blocker / Gatekeeper",
    difficulty: "Very Hard",
    focus_areas: ["Paper Process (P)", "Competition (C2)"],
    background: "Career procurement professional focused on cost reduction, vendor standardization, and contract compliance. Ben views specialized networks as overpriced and prefers working with established vendors.",
    approach: "A challenging conversation centered on price, terms, and process. Ben will try to commoditize your service and push for discounts. You'll need to justify premium positioning while showing flexibility on contract terms.",
    positive_triggers: ["Clear pricing", "Contract flexibility", "Risk mitigation"],
    negative_triggers: ["Unclear costs", "Rigid terms", "Dismissing procurement"]
  }
};

const SCENARIO_SUMMARY = {
  client: "Helios Capital Partners",
  clientDesc: "$2B Mid-Market Private Equity Fund",
  problem: "Their acquisition of Innovatech Dynamics is failing its 100-day plan. Supply chain chaos from a botched SensorTech integration is eroding margins by 8% instead of the planned 15% improvement.",
  yourRole: "Senior Partner at Apex Executive Network (AEN) -- an exclusive network of C-suite operators. Position AEN as the only logical solution. You are their peer, not a vendor.",
  keyPoints: [
    "Lead with execution, not advice -- operators who do, not slide decks",
    "Speed to impact -- weeks, not 6-9 months like consultants",
    "De-risk the investment -- proven track records in similar turnarounds",
  ]
};

export const ChatPanel = ({ messages, onSendMessage, sending, dealState, onSwitchToVoice }) => {
  const [input, setInput] = useState("");
  const [testModeOpen, setTestModeOpen] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [scenarioExpanded, setScenarioExpanded] = useState(false);
  const [personaExpanded, setPersonaExpanded] = useState(true);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !sending) {
      onSendMessage(input.trim());
      setInput("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTestResponse = async (quality) => {
    if (!dealState?.session_id) return;
    setLoadingSuggestion(quality);
    try {
      const response = await getSuggestion(dealState.session_id, quality);
      setInput(response.data.suggestion);
      setTestModeOpen(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    } catch (error) {
      console.error("Failed to get suggestion:", error);
      toast.error("Failed to generate suggestion");
    } finally {
      setLoadingSuggestion(null);
    }
  };

  const getPersonaInitials = (name) => {
    if (!name) return "AI";
    return name.split(" ").map(n => n[0]).join("").toUpperCase();
  };

  const getTrustDeltaIndicator = (delta) => {
    if (delta > 0) {
      return (
        <Badge variant="outline" className="ml-2 text-xs bg-emerald-50 text-emerald-600 border-emerald-200">
          <TrendingUp className="w-3 h-3 mr-0.5" />+{delta}
        </Badge>
      );
    } else if (delta < 0) {
      return (
        <Badge variant="outline" className="ml-2 text-xs bg-red-50 text-red-600 border-red-200">
          <TrendingDown className="w-3 h-3 mr-0.5" />{delta}
        </Badge>
      );
    }
    return null;
  };

  const getPersonaLabel = () => {
    switch (dealState?.active_persona) {
      case "jamie_sterling": return "Jamie";
      case "evelyn_reed": return "Evelyn";
      case "ben_carter": return "Ben";
      default: return "Persona";
    }
  };

  const isLoadingAny = loadingSuggestion !== null;
  const personaCtx = PERSONA_CONTEXT[dealState?.active_persona] || PERSONA_CONTEXT.jamie_sterling;

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900 text-sm">Helios Capital Meeting</h2>
            <p className="text-xs text-slate-400">
              Speaking with: {dealState?.active_persona === "jamie_sterling" ? "Jamie Sterling" :
                             dealState?.active_persona === "evelyn_reed" ? "Dr. Evelyn Reed" :
                             dealState?.active_persona === "ben_carter" ? "Ben Carter" : "Stakeholders"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Info toggle button - prominent */}
            <Button
              variant={infoOpen ? "default" : "outline"}
              size="sm"
              onClick={() => setInfoOpen(!infoOpen)}
              className={infoOpen ? 'bg-primary text-white' : 'border-primary/30 text-primary hover:bg-primary/5'}
              data-testid="info-pane-toggle"
            >
              {infoOpen ? <X className="w-3.5 h-3.5 mr-1" /> : <Info className="w-3.5 h-3.5 mr-1" />}
              {infoOpen ? "Close" : "Briefing"}
            </Button>
            <div className="flex -space-x-2">
              {dealState?.personas_introduced?.map((pid) => (
                <Avatar key={pid} className="w-7 h-7 border-2 border-white">
                  <AvatarImage src={PERSONA_IMAGES[pid]} />
                  <AvatarFallback className="text-xs bg-slate-100">
                    {pid === "jamie_sterling" ? "JS" : pid === "evelyn_reed" ? "ER" : "BC"}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Scenario & Persona Info Pane */}
      {infoOpen && (
        <div className="flex-shrink-0 border-b border-blue-100 animate-fade-in-up overflow-y-auto max-h-[45vh]" data-testid="scenario-info-pane">
          {/* Scenario Section */}
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <button
              className="w-full flex items-center justify-between"
              onClick={() => setScenarioExpanded(!scenarioExpanded)}
              data-testid="scenario-section-toggle"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Scenario: {SCENARIO_SUMMARY.client}</span>
              </div>
              {scenarioExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            </button>
            {scenarioExpanded && (
              <div className="mt-3 space-y-2.5 animate-fade-in-up">
                <p className="text-xs text-slate-500">{SCENARIO_SUMMARY.clientDesc}</p>
                <div className="p-2.5 bg-orange-50/80 rounded-lg border border-orange-100">
                  <p className="text-xs font-medium text-orange-700 mb-1">The Problem</p>
                  <p className="text-xs text-slate-600">{SCENARIO_SUMMARY.problem}</p>
                </div>
                <div className="p-2.5 bg-blue-50/80 rounded-lg border border-blue-100">
                  <p className="text-xs font-medium text-primary mb-1">Your Role</p>
                  <p className="text-xs text-slate-600">{SCENARIO_SUMMARY.yourRole}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-1">Key Talking Points</p>
                  <ul className="space-y-1">
                    {SCENARIO_SUMMARY.keyPoints.map((pt, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-slate-500">
                        <Zap className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Persona Section */}
          <div className="px-4 py-3 bg-blue-50/30">
            <button
              className="w-full flex items-center justify-between"
              onClick={() => setPersonaExpanded(!personaExpanded)}
              data-testid="persona-section-toggle"
            >
              <div className="flex items-center gap-2">
                <Avatar className="w-5 h-5">
                  <AvatarImage src={PERSONA_IMAGES[dealState?.active_persona]} />
                  <AvatarFallback className="text-[8px] bg-slate-100">{personaCtx.name.split(" ").map(n=>n[0]).join("")}</AvatarFallback>
                </Avatar>
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{personaCtx.name}</span>
                <Badge variant="outline" className="text-[10px] py-0 h-4 bg-white">{personaCtx.difficulty}</Badge>
              </div>
              {personaExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            </button>
            {personaExpanded && (
              <div className="mt-3 space-y-2.5 animate-fade-in-up">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400">{personaCtx.role}</span>
                  <span className="text-xs text-slate-300">|</span>
                  <span className="text-xs text-slate-500">{personaCtx.archetype}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {personaCtx.focus_areas.map((area, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{area}</span>
                  ))}
                </div>
                <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                  <p className="text-xs font-medium text-slate-700 mb-1">Background</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{personaCtx.background}</p>
                </div>
                <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                  <p className="text-xs font-medium text-slate-700 mb-1">What to Expect</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{personaCtx.approach}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-emerald-50/50 rounded-lg border border-emerald-100">
                    <p className="text-[10px] font-semibold text-emerald-600 mb-1">Builds Trust</p>
                    <ul className="space-y-0.5">
                      {personaCtx.positive_triggers.map((t, i) => (
                        <li key={i} className="text-[10px] text-slate-500 flex items-center gap-1">
                          <CheckCircle className="w-2.5 h-2.5 text-emerald-500" />{t}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-2 bg-red-50/50 rounded-lg border border-red-100">
                    <p className="text-[10px] font-semibold text-red-500 mb-1">Hurts Trust</p>
                    <ul className="space-y-0.5">
                      {personaCtx.negative_triggers.map((t, i) => (
                        <li key={i} className="text-[10px] text-slate-500 flex items-center gap-1">
                          <AlertTriangle className="w-2.5 h-2.5 text-red-400" />{t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={message.id || index}
              className={`flex animate-fade-in-up ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.role === "user" ? (
                <div className="flex items-end gap-2 max-w-[85%]">
                  <div className="chat-bubble-user" data-testid={`user-message-${index}`}>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                  <Avatar className="w-7 h-7 flex-shrink-0">
                    <AvatarFallback className="bg-primary text-white text-xs">
                      <User className="w-3.5 h-3.5" />
                    </AvatarFallback>
                  </Avatar>
                </div>
              ) : message.role === "system" ? (
                <div className="chat-bubble-system" data-testid={`system-message-${index}`}>
                  {message.content}
                </div>
              ) : (
                <div className="flex items-end gap-2 max-w-[85%]">
                  <Avatar className="w-7 h-7 flex-shrink-0">
                    <AvatarImage src={PERSONA_IMAGES[message.persona_id]} />
                    <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                      {getPersonaInitials(message.persona_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs font-medium text-slate-500">
                        {message.persona_name || "Buyer"}
                      </span>
                      {message.trust_delta !== undefined && message.trust_delta !== 0 && 
                        getTrustDeltaIndicator(message.trust_delta)
                      }
                    </div>
                    <div className="chat-bubble-persona" data-testid={`persona-message-${index}`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {sending && (
            <div className="flex justify-start animate-fade-in-up">
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-slate-100">
        <form onSubmit={handleSubmit} className="flex gap-2">
          {/* Test Mode Button */}
          <Popover open={testModeOpen} onOpenChange={setTestModeOpen}>
            <PopoverTrigger asChild>
              <Button 
                type="button"
                variant="outline" 
                size="lg"
                className="self-end px-3 border-purple-200 hover:border-purple-300 hover:bg-purple-50"
                disabled={sending || !dealState?.is_active || isLoadingAny}
                data-testid="test-mode-btn"
              >
                {isLoadingAny ? (
                  <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                ) : (
                  <FlaskConical className="w-5 h-5 text-purple-500" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" align="start" side="top">
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                  <FlaskConical className="w-4 h-4 text-purple-500" />
                  <span className="font-semibold text-sm text-slate-800">Test Mode</span>
                  <Badge variant="outline" className="ml-auto text-xs bg-slate-50">
                    for {getPersonaLabel()}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400">
                  AI-generated response based on current context:
                </p>
                <div className="space-y-2">
                  {[
                    { quality: "good", label: "Good Response", desc: "MEDDPICC-aligned, value-focused", color: "emerald", Icon: Sparkles },
                    { quality: "medium", label: "Medium Response", desc: "Generic, lacks specifics", color: "amber", Icon: Meh },
                    { quality: "bad", label: "Bad Response", desc: "Jargon, feature-dumping", color: "red", Icon: ThumbsDown },
                  ].map(({ quality, label, desc, color, Icon }) => (
                    <Button
                      key={quality}
                      type="button"
                      variant="outline"
                      className={`w-full justify-start gap-2 h-auto py-2.5 border-${color}-200 hover:bg-${color}-50 hover:border-${color}-300`}
                      onClick={() => handleTestResponse(quality)}
                      disabled={isLoadingAny}
                      data-testid={`test-${quality}-btn`}
                    >
                      <div className={`w-7 h-7 rounded-full bg-${color}-50 flex items-center justify-center flex-shrink-0`}>
                        {loadingSuggestion === quality ? (
                          <Loader2 className={`w-3.5 h-3.5 text-${color}-500 animate-spin`} />
                        ) : (
                          <Icon className={`w-3.5 h-3.5 text-${color}-500`} />
                        )}
                      </div>
                      <div className="text-left">
                        <p className={`font-medium text-sm text-${color}-600`}>{label}</p>
                        <p className="text-xs text-slate-400">{desc}</p>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message... (Enter to send)"
            className="min-h-[52px] max-h-[120px] resize-none border-slate-200 bg-slate-50 focus:bg-white"
            disabled={sending || !dealState?.is_active}
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            size="lg"
            disabled={!input.trim() || sending || !dealState?.is_active}
            className="self-end px-5 bg-primary hover:bg-primary/90"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
          {onSwitchToVoice && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={onSwitchToVoice}
              className="self-end px-3 border-violet-200 hover:border-violet-300 hover:bg-violet-50"
              disabled={sending || !dealState?.is_active}
              data-testid="voice-mode-btn"
            >
              <Mic className="w-5 h-5 text-violet-500" />
            </Button>
          )}
        </form>
        {!dealState?.is_active && (
          <p className="text-xs text-slate-400 mt-2 text-center">
            Simulation has ended. View your report for results.
          </p>
        )}
      </div>
    </div>
  );
};
